/**
 * Cookie Authentication Provider (Future Implementation)
 * This will be implemented when the API supports HTTP-only secure cookies
 * Placeholder for now to show the migration path
 */

import type {
  AuthStorageProvider,
  AuthTokens,
  AuthUser,
} from "../auth.interface";

export class CookieAuthProvider implements AuthStorageProvider {
  // User data will still be in localStorage, only tokens in cookies
  private readonly USER_DATA_KEY = "userData";
  private readonly REMEMBER_ME_KEY = "rememberMe";

  async setTokens(_tokens: AuthTokens): Promise<void> {
    // When API supports it, tokens will be set via Set-Cookie headers
    // This method might just validate that cookies were set
    throw new Error(
      "Cookie authentication not yet implemented - waiting for API support",
    );
  }

  async getTokens(): Promise<AuthTokens | null> {
    // Cookies will be sent automatically with requests
    // This might return null or a minimal object for compatibility
    // The actual token won't be accessible from JavaScript (HTTP-only)
    throw new Error(
      "Cookie authentication not yet implemented - waiting for API support",
    );
  }

  async clearTokens(): Promise<void> {
    // Will trigger an API call to clear server-side session
    throw new Error(
      "Cookie authentication not yet implemented - waiting for API support",
    );
  }

  async setUser(user: AuthUser): Promise<void> {
    // User info can still be in localStorage since it's not sensitive
    try {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(user));
    } catch {
      throw new Error("Failed to store user data");
    }
  }

  async getUser(): Promise<AuthUser | null> {
    const userData = localStorage.getItem(this.USER_DATA_KEY);
    if (!userData) {
      return null;
    }

    try {
      return JSON.parse(userData);
    } catch {
      await this.clearUser();
      return null;
    }
  }

  async clearUser(): Promise<void> {
    localStorage.removeItem(this.USER_DATA_KEY);
  }

  async hasAuth(): Promise<boolean> {
    // Will need to check cookie existence or make an API call
    throw new Error(
      "Cookie authentication not yet implemented - waiting for API support",
    );
  }

  async setRememberMe(value: boolean): Promise<void> {
    localStorage.setItem(this.REMEMBER_ME_KEY, value.toString());
  }

  async getRememberMe(): Promise<boolean> {
    const value = localStorage.getItem(this.REMEMBER_ME_KEY);
    return value === "true";
  }
}
